import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  getBillingAccount,
  getBillingPlans,
  getCreditBalance,
  isTokenGetterReady,
} from "@/services/api-client";
import {
  type BillingContextValue,
  CACHE_TTL,
  isAuthNotReadyError,
  isBillingAccountNotAvailableError,
  mapAccount,
  mapCredits,
  mapPlans,
  useTtlCachedFetch,
} from "../cache";

interface UseBillingCacheParams {
  autoFetch: boolean;
  requireAuth: boolean;
}

export function useBillingCache(
  { autoFetch, requireAuth }: UseBillingCacheParams,
): BillingContextValue {
  const { t } = useTranslation(["billing"]);
  const creditsResource = useTtlCachedFetch({
    ttlMs: CACHE_TTL.credits,
    fetcher: () => getCreditBalance(),
    mapData: mapCredits,
    errorFallbackMessage: t?.("billing:errors.fetchCreditBalance") ??
      "Failed to fetch credit balance",
    suppressErrorIf: isAuthNotReadyError,
  });
  const accountResource = useTtlCachedFetch({
    ttlMs: CACHE_TTL.account,
    fetcher: () => getBillingAccount(),
    mapData: mapAccount,
    errorFallbackMessage: t?.("billing:errors.fetchBillingAccount") ??
      "Failed to fetch billing account",
    suppressErrorIf: isAuthNotReadyError,
    treatErrorAsSuccessIf: isBillingAccountNotAvailableError,
  });
  const plansResource = useTtlCachedFetch({
    ttlMs: CACHE_TTL.plans,
    fetcher: () => getBillingPlans(),
    mapData: mapPlans,
    errorFallbackMessage: t?.("billing:errors.fetchBillingPlans") ??
      "Failed to fetch billing plans",
    suppressErrorIf: isAuthNotReadyError,
  });
  const { refresh: refreshCredits, invalidate: invalidateCredits } = creditsResource;
  const { refresh: refreshAccount, invalidate: invalidateAccount } = accountResource;
  const { refresh: refreshPlans, invalidate: invalidatePlans } = plansResource;

  const refreshAll = useCallback(async (force = false) => {
    await Promise.all([
      refreshCredits(force),
      refreshAccount(force),
      refreshPlans(force),
    ]);
  }, [refreshAccount, refreshCredits, refreshPlans]);

  const invalidateAll = useCallback(() => {
    invalidateCredits();
    invalidateAccount();
    invalidatePlans();
  }, [invalidateAccount, invalidateCredits, invalidatePlans]);

  useEffect(() => {
    if (!autoFetch) return;
    if (requireAuth && !isTokenGetterReady()) return;
    void refreshAll();
  }, [autoFetch, refreshAll, requireAuth]);

  return {
    credits: creditsResource.data,
    creditsLoadingState: creditsResource.loadingState,
    creditsLastFetched: creditsResource.lastFetched,
    creditsError: creditsResource.error,
    account: accountResource.data,
    accountLoadingState: accountResource.loadingState,
    accountLastFetched: accountResource.lastFetched,
    accountError: accountResource.error,
    plans: plansResource.data,
    plansLoadingState: plansResource.loadingState,
    plansLastFetched: plansResource.lastFetched,
    plansError: plansResource.error,
    refreshCredits: creditsResource.refresh,
    refreshAccount: accountResource.refresh,
    refreshPlans: plansResource.refresh,
    refreshAll,
    invalidateCredits: creditsResource.invalidate,
    invalidateAccount: accountResource.invalidate,
    invalidatePlans: plansResource.invalidate,
    invalidateAll,
  };
}
